import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Layout } from '~/components/layouts/layout';
import { m } from '~/paraglide/messages.js';
import { fetchJson } from '~/utils/fetch-json-v2';

export const Route = createFileRoute('/_authenticated/dev/accounts/')({
  component: DevAccountsPage,
});

const LIMIT = 10;

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface AccountsResult {
  accounts: Record<string, unknown>[];
  pagination: Pagination;
}

interface AccountsFilters {
  type: string;
  status: string;
  urn: string;
}

const statusOptions = [
  { value: 'all', label: m['common.all']() },
  { value: 'disabled', label: m['developer.dash_accounts.status.disabled']() },
  { value: 'active', label: m['common.status.active']() },
  {
    value: 'creation_in_progress',
    label: m['developer.dash_accounts.status.creation_in_progress'](),
  },
  { value: 'frozen', label: m['developer.dash_accounts.status.frozen']() },
  {
    value: 'creation_failed',
    label: m['developer.dash_accounts.status.creation_failed'](),
  },
];

const toText = (value: unknown, fallback = '-') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  return fallback;
};

const firstValue = (item: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }

  return null;
};

const accountName = (account: Record<string, unknown>) => {
  const metadata =
    account.metadata && typeof account.metadata === 'object'
      ? (account.metadata as Record<string, unknown>)
      : null;

  if (metadata?.name !== undefined && metadata?.name !== null) {
    return metadata.name;
  }

  return firstValue(account, ['name', 'alias', 'title']);
};

const toPagination = (
  payload: Record<string, unknown>,
  page: number,
  limit: number,
  total: number,
): Pagination => {
  const data = (payload.pagination ?? {}) as Record<string, unknown>;
  const payloadPage = Number(data.page);
  const payloadLimit = Number(data.limit);
  const payloadTotal = Number(data.total);
  const payloadTotalPages = Number(data.totalPages);

  const safePage = Number.isFinite(payloadPage) ? payloadPage : page;
  const safeLimit = Number.isFinite(payloadLimit) ? payloadLimit : limit;
  const safeTotal = Number.isFinite(payloadTotal) ? payloadTotal : total;
  const calculatedPages = safeLimit > 0 ? Math.ceil(safeTotal / safeLimit) : 1;
  const safeTotalPages = Number.isFinite(payloadTotalPages)
    ? payloadTotalPages
    : Math.max(calculatedPages, 1);

  return {
    page: safePage,
    limit: safeLimit,
    total: safeTotal,
    totalPages: safeTotalPages,
  };
};

const toAccountsList = (payload: Record<string, unknown>) => {
  const candidates = [payload.data, payload.accounts, payload.results];
  const list = candidates.find((value) => Array.isArray(value));
  return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
};

async function getAccountTypes(): Promise<string[]> {
  const result = await fetchJson<
    { data?: { name?: string }[] } | { name?: string }[]
  >('/v2/dash/accounts/types');

  if (result.error || !result.data) {
    throw new Error(result.error ?? 'Error al listar tipos de cuenta');
  }

  const payload = result.data;
  const list = Array.isArray(payload) ? payload : payload.data;

  if (!Array.isArray(list)) return [];

  return list
    .map((item) => (typeof item?.name === 'string' ? item.name : ''))
    .filter((type) => type.length > 0);
}

async function getAccounts(
  page: number,
  limit: number,
  filters: AccountsFilters,
): Promise<AccountsResult> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (filters.type !== 'all') {
    params.set('type', filters.type);
  }

  if (filters.status !== 'all') {
    params.set('status', filters.status);
  }

  if (filters.urn.trim().length > 0) {
    params.set('urn', filters.urn.trim());
  }

  const result = await fetchJson<Record<string, unknown>>(
    `/v2/dash/accounts?${params.toString()}`,
  );

  if (result.error || !result.data) {
    throw new Error(result.error ?? 'Error al listar cuentas');
  }

  const payload = result.data;
  const accounts = toAccountsList(payload);

  return {
    accounts,
    pagination: toPagination(payload, page, limit, accounts.length),
  };
}

function DevAccountsPage() {
  const [page, setPage] = useState(1);
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [urn, setUrn] = useState('');
  const [filters, setFilters] = useState<AccountsFilters>({
    type: 'all',
    status: 'all',
    urn: '',
  });

  const { data: accountTypes = [] } = useQuery({
    queryKey: ['dev-dash-account-types'],
    queryFn: getAccountTypes,
  });

  const { data, isPending, error } = useQuery({
    queryKey: [
      'dev-dash-accounts',
      page,
      LIMIT,
      filters.type,
      filters.status,
      filters.urn,
    ],
    queryFn: () => getAccounts(page, LIMIT, filters),
  });

  const accounts = data?.accounts ?? [];
  const pagination = data?.pagination ?? {
    page: 1,
    limit: LIMIT,
    total: 0,
    totalPages: 1,
  };

  const handleApplyFilters = () => {
    setFilters({ type, status, urn });
    setPage(1);
  };

  return (
    <Layout
      title={m['developer.dash_accounts.title']()}
      breadcrumbs={[
        { label: m['developer.menu.title'](), href: '/dev' },
        {
          label: m['developer.dash_accounts.title'](),
          href: '/dev/accounts',
        },
      ]}
    >
      <Card>
        <CardHeader>
          <CardTitle>{m['developer.dash_accounts.title']()}</CardTitle>
          <CardDescription>
            {m['developer.dash_accounts.subtitle']()}
          </CardDescription>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="type">
                {m['developer.dash_accounts.type']()}
              </Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger id="type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{m['common.all']()}</SelectItem>
                  {accountTypes.map((accountType) => (
                    <SelectItem key={accountType} value={accountType}>
                      {accountType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">
                {m['developer.dash_accounts.status_label']()}
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="urn">{m['developer.dash_accounts.urn']()}</Label>
              <Input
                id="urn"
                value={urn}
                onChange={(event) => setUrn(event.target.value)}
                placeholder={m['developer.dash_accounts.urn_placeholder']()}
              />
            </div>

            <div className="space-y-2">
              <Label className="invisible">{m['common.actions']()}</Label>
              <Button onClick={handleApplyFilters} className="w-full">
                {m['developer.dash_accounts.apply_filters']()}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-red-500">
              {error.message ?? m['developer.dash_accounts.error_loading']()}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{m['common.name']()}</TableHead>
                      <TableHead>URN</TableHead>
                      <TableHead>
                        {m['developer.dash_accounts.type']()}
                      </TableHead>
                      <TableHead>
                        {m['developer.dash_accounts.status_label']()}
                      </TableHead>
                      <TableHead>
                        {m['developer.dash_accounts.balance']()}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isPending
                      ? Array.from({ length: LIMIT }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            {Array.from({ length: 5 }).map((__, cellIndex) => (
                              <TableCell key={`cell-${cellIndex}`}>
                                <Skeleton className="h-4 w-full" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : accounts.map((account, index) => (
                          <TableRow
                            key={toText(
                              firstValue(account, [
                                'urn',
                                'id',
                                'uuid',
                                'account_urn',
                              ]),
                              `account-${index}`,
                            )}
                          >
                            <TableCell>
                              {toText(accountName(account))}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {toText(
                                firstValue(account, ['urn', 'account_urn']),
                              )}
                            </TableCell>
                            <TableCell>
                              {toText(firstValue(account, ['medium']))}
                            </TableCell>
                            <TableCell>
                              {toText(firstValue(account, ['status']))}
                            </TableCell>
                            <TableCell>
                              {toText(
                                firstValue(account, [
                                  'balance',
                                  'amount',
                                  'available_balance',
                                ]),
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    {!isPending && accounts.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground py-8"
                        >
                          {m['developer.dash_accounts.empty_message']()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {m['common.table.page_x_of_y']({
                    current: pagination.page,
                    total: pagination.totalPages,
                  })}{' '}
                  | {m['common.total']()}: {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setPage((current) => Math.max(current - 1, 1))
                    }
                    disabled={pagination.page <= 1 || isPending}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setPage((current) =>
                        Math.min(current + 1, pagination.totalPages),
                      )
                    }
                    disabled={
                      pagination.page >= pagination.totalPages || isPending
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
